<script setup lang="ts">
import {
  CLIENT_RELATION_LABELS,
  formatEuros,
  searchClients,
  type ClientDirectoryItem,
  type SubcontractorDirectoryItem,
} from '@cockpit/shared';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId } from 'vue';
import { useRouter } from 'vue-router';
import { clientsApi } from '../api/clients';
import { subcontractingApi } from '../api/subcontracting';
import { errorText } from '../composables/useResource';
import '../styles/client.css';
import '../styles/subcontracting.css';

/**
 * Lot AV : recherche de client de l'en-tête (nom ou commune), vers la page du client. Motif ARIA « combobox » +
 * « listbox » : ↑/↓ pour parcourir, Entrée pour ouvrir, Échap pour fermer ; Ctrl + K (⌘ + K sur Mac) ou « / »
 * place le curseur dans la recherche depuis n'importe quel écran. Sur téléphone, une loupe ouvre le champ.
 * L'annuaire (`GET /api/clients/directory`) est lu une fois, à la première utilisation. Lot AY : les sous-traitants
 * (fournisseurs Axonaut classés sous-traitants, `GET /api/subcontractors/directory`) sont trouvés aussi, marqués
 * « Sous-traitant », vers leur fiche ; les autres fournisseurs ne remontent pas.
 */
const router = useRouter();
const uid = useId();
const listId = `csearch-list-${uid}`;
const optionId = (index: number) => `csearch-opt-${uid}-${index}`;

const directory = ref<ClientDirectoryItem[] | null>(null);
/** Lot AY : sous-traitants (une panne de leur lecture n'empêche pas la recherche des clients). */
const subcontractors = ref<SubcontractorDirectoryItem[]>([]);

type Found =
  | { kind: 'client'; key: string; name: string; city: string | null; item: ClientDirectoryItem }
  | { kind: 'sub'; key: string; name: string; city: null; item: SubcontractorDirectoryItem };
const loadError = ref('');
const query = ref('');
const open = ref(false);
/** Téléphone : le champ n'est visible qu'après un appui sur la loupe. */
const expanded = ref(false);
const active = ref(0);
const input = ref<HTMLInputElement | null>(null);
const root = ref<HTMLElement | null>(null);

let loading: Promise<void> | null = null;
function ensureDirectory(): Promise<void> {
  if (directory.value) return Promise.resolve();
  loading ??= Promise.all([
    clientsApi.directory(),
    subcontractingApi.directory().catch(() => ({ items: [] as SubcontractorDirectoryItem[] })),
  ])
    .then(([res, subs]) => {
      directory.value = res.items;
      subcontractors.value = subs.items;
      loadError.value = '';
    })
    .catch((err) => {
      loadError.value = errorText(err);
    })
    .finally(() => {
      loading = null;
    });
  return loading;
}

const results = computed(() =>
  searchClients<Found>(
    [
      ...(directory.value ?? []).map((item): Found => ({
        kind: 'client',
        key: `c:${item.id}`,
        name: item.name,
        city: item.city,
        item,
      })),
      ...subcontractors.value.map((item): Found => ({
        kind: 'sub',
        key: `s:${item.id}`,
        name: item.name,
        city: null,
        item,
      })),
    ],
    query.value,
    8,
  ),
);
const showList = computed(() => open.value && query.value.trim() !== '');
const status = computed(() => {
  if (!showList.value) return '';
  if (loadError.value) return loadError.value;
  if (!directory.value) return 'Chargement des clients…';
  return results.value.length === 0
    ? 'Aucun client trouvé'
    : `${results.value.length} résultat${results.value.length > 1 ? 's' : ''}`;
});

function onInput(): void {
  open.value = true;
  active.value = 0;
  void ensureDirectory();
}

async function choose(found: Found | undefined): Promise<void> {
  if (!found) return;
  close(true);
  if (found.kind === 'sub') await router.push({ name: 'subcontractor', params: { id: found.item.id } });
  else await router.push({ name: 'client', params: { id: found.item.id } });
}

/** « 4 devis · 7 400 € confiés en 2026 » */
function subLine(item: SubcontractorDirectoryItem): string {
  return item.quotesCount > 0
    ? `${item.quotesCount} devis · ${formatEuros(item.confiedCents)} confiés en ${item.year}`
    : 'fournisseur Axonaut';
}

function close(clear = false): void {
  open.value = false;
  if (clear) {
    query.value = '';
    expanded.value = false;
    input.value?.blur();
  }
}

function onKeydown(event: KeyboardEvent): void {
  const count = results.value.length;
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    open.value = true;
    if (count > 0) active.value = (active.value + 1) % count;
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    if (count > 0) active.value = (active.value - 1 + count) % count;
  } else if (event.key === 'Enter') {
    if (showList.value && count > 0) {
      event.preventDefault();
      void choose(results.value[active.value] ?? results.value[0]);
    }
  } else if (event.key === 'Escape') {
    if (open.value && query.value) {
      event.stopPropagation();
      open.value = false;
    } else {
      close(true);
    }
  }
}

async function expand(): Promise<void> {
  expanded.value = true;
  await nextTick();
  input.value?.focus();
  void ensureDirectory();
}

/** Ctrl / ⌘ + K, ou « / » hors d'un champ de saisie : place le curseur dans la recherche. */
function onGlobalKeydown(event: KeyboardEvent): void {
  const target = event.target as HTMLElement | null;
  const typing =
    !!target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
  const shortcut =
    ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') || (event.key === '/' && !typing);
  if (!shortcut || event.altKey) return;
  event.preventDefault();
  void expand();
}
function onDocumentClick(event: MouseEvent): void {
  if (root.value && !root.value.contains(event.target as Node)) {
    open.value = false;
    if (!query.value) expanded.value = false;
  }
}
onMounted(() => {
  document.addEventListener('keydown', onGlobalKeydown);
  document.addEventListener('click', onDocumentClick);
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onGlobalKeydown);
  document.removeEventListener('click', onDocumentClick);
});

const relationClass: Record<ClientDirectoryItem['relation'], string> = {
  client: 'c-ok',
  former: 'c-mut',
  prospect: 'c-acc',
};
</script>

<template>
  <div ref="root" class="csearch" :class="{ expanded }" data-testid="client-search">
    <button
      class="btn sm csearch-icon"
      type="button"
      aria-label="Chercher un client"
      data-testid="client-search-open"
      @click="expand"
    >
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5L14 14" stroke-linecap="round" />
      </svg>
    </button>
    <div class="csearch-field">
      <input
        ref="input"
        v-model="query"
        type="search"
        class="csearch-input"
        placeholder="Chercher un client…"
        aria-label="Chercher un client (nom ou commune)"
        role="combobox"
        autocomplete="off"
        spellcheck="false"
        :aria-expanded="showList ? 'true' : 'false'"
        :aria-controls="listId"
        aria-autocomplete="list"
        :aria-activedescendant="showList && results.length > 0 ? optionId(active) : undefined"
        data-testid="client-search-input"
        @focus="ensureDirectory"
        @input="onInput"
        @keydown="onKeydown"
      />
      <kbd class="csearch-kbd" aria-hidden="true">Ctrl K</kbd>
      <ul v-show="showList" :id="listId" class="csearch-list" role="listbox" aria-label="Clients trouvés">
        <li
          v-for="(found, index) in results"
          :id="optionId(index)"
          :key="found.key"
          role="option"
          class="csearch-option"
          :class="{ active: index === active }"
          :aria-selected="index === active ? 'true' : 'false'"
          :data-testid="found.kind === 'sub' ? 'subcontractor-search-option' : 'client-search-option'"
          @mousedown.prevent
          @mouseenter="active = index"
          @click="choose(found)"
        >
          <span class="csearch-name">{{ found.name }}</span>
          <template v-if="found.kind === 'client'">
            <small v-if="found.city" class="csearch-city">{{ found.city }}</small>
            <span class="chip" :class="relationClass[found.item.relation]">{{
              CLIENT_RELATION_LABELS[found.item.relation]
            }}</span>
          </template>
          <template v-else>
            <small class="csearch-city">{{ subLine(found.item) }}</small>
            <span class="sub-chip">Sous-traitant</span>
          </template>
        </li>
        <li v-if="results.length === 0" class="csearch-empty" role="presentation">
          {{ loadError || (directory ? 'Aucun client trouvé.' : 'Chargement des clients…') }}
        </li>
      </ul>
      <span class="sr-only" aria-live="polite">{{ status }}</span>
    </div>
  </div>
</template>
